import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Switch } from '@/components/ui/switch';
import AnimatedCounter from './AnimatedCounter';

export default function ProgressiveReduction({ steps = [] }) {
  const [enabled, setEnabled] = useState(() => {
    const obj = {};
    steps.forEach((s) => { obj[s.key] = s.default_on ?? true; });
    return obj;
  });

  const final = useMemo(() => {
    let running = 0;
    steps.forEach((s) => {
      if (s.key === 'all') {
        running = enabled[s.key] ? Math.abs(s.delta) : 0;
      } else if (enabled[s.key]) {
        running = Math.max(0, running + s.delta);
      }
    });
    return running;
  }, [enabled, steps]);

  const initial = steps[0]?.delta ?? 0;
  const reductionPct = initial > 0 ? Math.round((1 - final / initial) * 100) : 0;

  return (
    <div className="space-y-1" data-testid="progressive-reduction">
      <div className="flex flex-col gap-1">
        {steps.map((s, i) => {
          const isFirst = i === 0;
          const isLast = i === steps.length - 1;
          let display;
          if (isFirst) {
            display = enabled[s.key] ? Math.abs(s.delta) : 0;
          } else {
            // Recompute running count up to this step
            let r = 0;
            for (let j = 0; j <= i; j++) {
              const sj = steps[j];
              if (sj.key === 'all') r = enabled[sj.key] ? Math.abs(sj.delta) : 0;
              else if (enabled[sj.key]) r = Math.max(0, r + sj.delta);
            }
            display = r;
          }
          return (
            <div
              key={s.key}
              className={`flex items-center justify-between gap-3 px-4 py-3 rounded-md border transition-all ${enabled[s.key] ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 opacity-60'}`}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Switch
                  checked={enabled[s.key]}
                  onCheckedChange={(v) => setEnabled((prev) => ({ ...prev, [s.key]: v }))}
                  data-testid={`waterfall-toggle-${s.key}`}
                  className="data-[state=checked]:bg-apex-lens"
                />
                <span className="text-sm text-apex-ink-2 truncate">{s.label}</span>
              </div>
              <div className="text-right shrink-0">
                <div className={`tabular text-base font-semibold font-mono ${isLast && enabled[s.key] ? 'text-apex-lens' : 'text-apex-ink'}`}>
                  <AnimatedCounter key={`${s.key}-${display}-${enabled[s.key]}`} value={display} duration={0.6} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <AnimatePresence>
        <motion.div
          key={final}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3 flex items-center justify-between border-t border-dashed border-slate-300 pt-3"
        >
          <div>
            <div className="text-[10px] uppercase tracking-wider text-apex-neutral">Governance actions required</div>
            <div className="text-3xl font-bold text-apex-lens font-mono tabular">
              <AnimatedCounter value={final} duration={0.7} />
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-apex-neutral">Noise reduction</div>
            <div className="text-2xl font-bold text-apex-success tabular">{reductionPct}%</div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
